import styled, { css } from 'styled-components';

import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import {
  scale130,
  scale150,
  scale180,
  scale220,
  spaceL,
  spaceXL,
  xdlColorText,
} from '@xing-com/tokens';

import type { HeroSize } from './hero.types';

type HeroTProps = {
  $noMargin?: boolean;
  $size: HeroSize;
  $sizeConfined?: HeroSize;
  $sizeWide?: HeroSize;
};

// *** Base ***
const baseHero = css`
  color: ${xdlColorText};
  font-weight: 700;
  margin-left: 0;
  margin-right: 0;
  margin-top: 0;
  padding: 0;
`;

const noMargin = css`
  && {
    margin: 0;
  }
`;

// *** Sizes ***
export const sizes = {
  small: css`
    font-size: ${scale130};
    line-height: 1.2;
    margin-bottom: ${spaceL};
  `,
  medium: css`
    font-size: ${scale150};
    line-height: 1.2;
    margin-bottom: ${spaceXL};
  `,
  large: css`
    font-size: ${scale180};
    line-height: 1.1;
    margin-bottom: ${spaceXL};
  `,
  xlarge: css`
    font-size: ${scale220};
    line-height: 1.1;
    margin-bottom: ${spaceXL};
  `,
};

// *** Components ***
const Hero = styled.h1<HeroTProps>`
  ${baseHero};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      case 'large':
        return sizes.large;
      case 'xlarge':
        return sizes.xlarge;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaConfined} {
            ${sizes.xlarge}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${sizes.large}
          }
        `;
      case 'xlarge':
        return css`
          @media ${mediaWide} {
            ${sizes.xlarge}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $noMargin }) => $noMargin && noMargin};
`;

export const Styled = {
  Hero,
};
